import { useState, useEffect, useCallback } from 'react';

import { supabase } from '../lib/supabase';
import { logEvent } from '../services/loggingService';

export interface UserPreferences {
  id: string;
  user_id: string;
  app_tour_complete: boolean;
  onboarding_complete: boolean;
  tour_completed_at: string | null;
  onboarding_completed_at: string | null;
  tour_disabled_permanently?: boolean; // Flag to bypass app tour completely
  contextual_tour_preferences: {
    frequency: 'high' | 'medium' | 'low' | 'disabled';
    seenTooltips: string[];
    lastInteraction: number;
  };
  notification_preferences: {
    product_updates: boolean;
    marketing_emails: boolean;
    push_notifications: boolean;
    email_notifications: boolean;
  };
  ui_preferences: {
    theme: 'dark' | 'light' | 'system';
    language: string;
    timezone: string;
    dashboard_layout: 'default' | 'compact';
    /** Ordered list of dashboard card ids for custom layout (Edit Layout). */
    dashboard_card_order?: string[];
  };
  session_preferences?: {
    timeout_enabled: boolean;
    timeout_minutes: number;
    warning_minutes: number;
  };
  created_at: string;
  updated_at: string;
}

export const useUserPreferences = () => {
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch user preferences
  const fetchPreferences = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setPreferences(null);
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('user_preferences')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching preferences:', error);
        setError(error.message);
        return;
      }

      setPreferences(data);

    } catch (err) {
      console.error('Error in fetchPreferences:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch preferences');
    } finally {
      setLoading(false);
    }
  }, []);

  // Update a single preference
  const updatePreference = useCallback(async (updates: Partial<UserPreferences>) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      const { error } = await supabase
        .from('user_preferences')
        .upsert({
          user_id: user.id,
          ...updates,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id'
        });

      if (error) throw error;

      // Refresh preferences
      await fetchPreferences();

      await logEvent({
        level: 'info',
        action: 'user_preferences_updated',
        details: { updatedFields: Object.keys(updates) }
      });

      return true;

    } catch (err) {
      console.error('Error updating preferences:', err);
      setError(err instanceof Error ? err.message : 'Failed to update preferences');
      return false;
    }
  }, [fetchPreferences]);

  // Update contextual tour preferences
  const updateContextualTourPreferences = useCallback(async (updates: Partial<UserPreferences['contextual_tour_preferences']>) => {
    if (!preferences) return false;

    const newContextualPrefs = {
      ...preferences.contextual_tour_preferences,
      ...updates
    };

    return updatePreference({ contextual_tour_preferences: newContextualPrefs });
  }, [preferences, updatePreference]);

  // Update notification preferences
  const updateNotificationPreferences = useCallback(async (updates: Partial<UserPreferences['notification_preferences']>) => {
    if (!preferences) return false;

    const newNotificationPrefs = {
      ...preferences.notification_preferences,
      ...updates
    };

    return updatePreference({ notification_preferences: newNotificationPrefs });
  }, [preferences, updatePreference]);

  // Update UI preferences
  const updateUIPreferences = useCallback(async (updates: Partial<UserPreferences['ui_preferences']>) => {
    if (!preferences) return false;

    const newUIPrefs = {
      ...preferences.ui_preferences,
      ...updates
    };

    return updatePreference({ ui_preferences: newUIPrefs });
  }, [preferences, updatePreference]);

  // Update session timeout preferences
  const updateSessionPreferences = useCallback(async (updates: {
    timeout_enabled: boolean;
    timeout_minutes: number;
    warning_minutes: number;
  }) => {
    if (!preferences) return false;

    const newSessionPrefs = {
      ...(preferences.session_preferences ?? {
        timeout_enabled: true,
        timeout_minutes: 30,
        warning_minutes: 5,
      }),
      ...updates,
    };

    return updatePreference({ session_preferences: newSessionPrefs });
  }, [preferences, updatePreference]);

  // Load preferences on mount
  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  // Computed values
  const needsOnboarding = !loading && preferences && !preferences.onboarding_complete;

  return {
    preferences,
    loading,
    error,
    fetchPreferences,
    updatePreference,
    updateContextualTourPreferences,
    updateNotificationPreferences,
    updateUIPreferences,
    updateSessionPreferences,
    needsOnboarding
  };
};
